import React, { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  FormControl,
  Link,
  Grid,
  CircularProgress,
  Alert,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import CustomCheckbox from '../../../common/components/CustomCheckbox/CustomCheckbox';
import { Sidebar } from '../../../common/components/Sidebar';
import SectionBar from '../../../common/components/SectionBar/SectionBar';
import { useNavItems } from '../../../hooks';
import { useUser } from '../../../hooks/useUser';
import { useModuleOperators } from '../../../hooks/useModuleOperators';
import { useNO4Report } from './hooks';
import NotificationSnackbar from '../../../common/components/Snackbar/Snackbar';
import { NoDataFound } from '../../../common/components/NoDataFound';
import NO4ReportTable from './components/NO4ReportTable';
import {
  MainContent,
  ReportContainer,
  SectionTitle,
  CheckboxGroup,
  ButtonContainer,
  FilterSection,
  SelectContainer,
  StyledSegmentedControl,
  StyledSegmentButton,
  ClearResultsButton,
  ResetButton,
  ExportButton,
} from './NO4Report.styled';

interface ServiceTypeState {
  aircraftPriority: boolean;
  salNonPriority: boolean;
  surfaceNonPriority: boolean;
  surfacePriority: boolean;
}

interface AccountingTypeState {
  gross: boolean;
  net: boolean;
}

interface DirectionOption {
  id: string;
  label: string;
}

// Utility function to compare year-quarter combinations
const compareYearQuarter = (year1: string, quarter1: string, year2: string, quarter2: string): number => {
  const y1 = parseInt(year1);
  const y2 = parseInt(year2);
  const q1 = parseInt(quarter1.replace('Q', ''));
  const q2 = parseInt(quarter2.replace('Q', ''));
  
  if (y1 !== y2) {
    return y1 - y2; // Compare years first
  }
  return q1 - q2; // If years are equal, compare quarters
};

// Utility function to determine direction based on current user's designated operator and receiving PPO
const determineDirection = (
  userDirection: string,
  currentUserDesignatedOperator: string | undefined,
  receivingPpo: string | undefined
): string => {
  // If user explicitly chose Inbound or Outbound, respect that choice
  if (userDirection === 'Inbound' || userDirection === 'Outbound') {
    return userDirection;
  }
  
  // If user chose "All", determine direction based on receiving PPO vs user's designated operator
  if (userDirection === 'All' && currentUserDesignatedOperator && receivingPpo) {
    // If receiving PPO matches current user's designated operator, it's inbound
    // Otherwise, it's outbound
    return receivingPpo === currentUserDesignatedOperator ? 'Inbound' : 'Outbound';
  }
  
  // Default fallback
  return userDirection;
};

function NO4Report() {
  const [sidebarOpen, setSidebarOpen] = useState<boolean>(true);
  const navigate = useNavigate();
  const navItems = useNavItems();
  const [searchParams, setSearchParams] = useSearchParams();
  
  // Get current user information
  const { data: currentUser } = useUser();
  
  // Get current user's designated operator
  const currentUserDesignatedOperator = React.useMemo(() => {
    return currentUser?.designatedOperator;
  }, [currentUser]);

  const [ppoValue, setPpoValue] = useState('');
  const [ataFilter, setAtaFilter] = useState('All');
  const [selectedYear, setSelectedYear] = useState('');

  // Add state for HTML results
  const [htmlResults, setHtmlResults] = useState<any[] | null>(null);
  // Add state to track when no data is found
  const [noDataFound, setNoDataFound] = useState<boolean>(false);
  // Add state to track the direction used for current results
  const [resultsDirection, setResultsDirection] = useState<string>('');

  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'error' as 'error' | 'warning' | 'info' | 'success',
  });

  // Get effective operators + cascading logic for eno1 module (IPC TD Accounting)
  const {
    operators,
    availableYears,
    operatorsForYear,
    yearsForOperator,
    relationsData: allRelationsData,
    isPpoLoading,
    ppoError,
  } = useModuleOperators('eno1', { selectedYear, selectedPpo: ppoValue });

  // NO4 Report hook
  const {
    mutate: generateReport,
    isPending: isGeneratingReport,
    error: reportError,
    reset: resetReportError,
  } = useNO4Report();

  // Map the target DOs to PPO options with cascading logic
  const ppoOptions = React.useMemo(() => {
    const options: { id: string; label: string; hasLicense?: boolean }[] = [];
    const targetDOsToUse = selectedYear ? operatorsForYear : operators;
    targetDOsToUse.forEach((targetDO) => {
      options.push({ id: targetDO, label: targetDO, hasLicense: true });
    });
    return options;
  }, [operators, operatorsForYear, selectedYear]);

  // Generate year options with cascading logic
  const yearOptions = React.useMemo(() => {
    const yearsToShow = ppoValue ? yearsForOperator : availableYears;
    return yearsToShow.map(year => ({ value: year.toString(), label: year.toString() }));
  }, [availableYears, yearsForOperator, ppoValue]);

  // Service Type state
  const [serviceType, setServiceType] = useState<ServiceTypeState>({
    aircraftPriority: true,
    salNonPriority: true,
    surfaceNonPriority: true,
    surfacePriority: true,
  });

  // Accounting Type state
  const [accountingType, setAccountingType] = useState<AccountingTypeState>({
    gross: true,
    net: true,
  });

  // Direction filter options
  const directionOptions: DirectionOption[] = [
    { id: 'All', label: 'All' },
    { id: 'Inbound', label: 'Inbound' },
    { id: 'Outbound', label: 'Outbound' },
  ];

  const handleSidebarToggle = () => {
    setSidebarOpen((prev) => !prev);
  };

  const handleSubtitleClick = () => {
    navigate('/ipctdaccounting');
  };

  const handleDirectionChange = (id: string) => {
    setAtaFilter(id);
  };

  // Handle DO selection change — years are independent of the selected DO
  const handleDOChange = useCallback((value: string) => {
    setPpoValue(value);
  }, []);

  // Handle year selection change with cascading behavior
  const handleYearChange = useCallback((value: string) => {
    // Only clear DO selection if the currently selected DO is not available for the new year
    if (selectedYear !== value && ppoValue) {
      // Check if the current DO is available for the new year
      const relationsForNewYear = allRelationsData?.byYear?.[value];
      const isCurrentDOAvailableForNewYear = relationsForNewYear?.some(relation => 
        relation.targetDO === ppoValue &&
        relation.modules.some(module => 
          module.name === 'eno1' && module.enabled === true
        )
      );
      
      if (!isCurrentDOAvailableForNewYear) {
        setPpoValue('');
      }
    }
    setSelectedYear(value);
  }, [selectedYear, ppoValue, allRelationsData]);

  const handleSnackbarClose = (event?: React.SyntheticEvent | Event, reason?: string) => {
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const showSnackbar = (
    message: string,
    severity: 'error' | 'warning' | 'info' | 'success' = 'error'
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const handleServiceTypeChange =
    (field: keyof ServiceTypeState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setServiceType((prev) => ({
        ...prev,
        [field]: event.target.checked,
      }));
    };

  const handleAccountingTypeChange =
    (field: keyof AccountingTypeState) => (event: React.ChangeEvent<HTMLInputElement>) => {
      setAccountingType((prev) => ({
        ...prev,
        [field]: event.target.checked,
      }));
    };

  const handleSelectAllServiceType = () => {
    const allSelected =
      serviceType.aircraftPriority &&
      serviceType.salNonPriority &&
      serviceType.surfaceNonPriority &&
      serviceType.surfacePriority;

    if (allSelected) {
      // Deselect all
      setServiceType({
        aircraftPriority: false,
        salNonPriority: false,
        surfaceNonPriority: false,
        surfacePriority: false,
      });
    } else {
      // Select all
      setServiceType({
        aircraftPriority: true,
        salNonPriority: true,
        surfaceNonPriority: true,
        surfacePriority: true,
      });
    }
  };

  const handleSelectAllAccountingType = () => {
    const allSelected = accountingType.gross && accountingType.net;

    if (allSelected) {
      // Deselect all
      setAccountingType({
        gross: false,
        net: false,
      });
    } else {
      // Select all
      setAccountingType({
        gross: true,
        net: true,
      });
    }
  };

  const handleReset = () => {
    setPpoValue('');
    setAtaFilter('All');
    setSelectedYear('');
    setServiceType({
      aircraftPriority: true,
      salNonPriority: true,
      surfaceNonPriority: true,
      surfacePriority: true,
    });
    setAccountingType({
      gross: true,
      net: true,
    });
    // Clear no data found state and results when resetting
    setNoDataFound(false);
    setHtmlResults(null);
    setResultsDirection('');
  };

  // Map checkbox states to API parameter values
  const getMailCategoryValues = (): string[] => {
    const values: string[] = [];
    if (serviceType.aircraftPriority) values.push('A');
    if (serviceType.salNonPriority) values.push('B');
    if (serviceType.surfaceNonPriority) values.push('C');
    if (serviceType.surfacePriority) values.push('D');
    return values;
  };

  const getAccountingTypeValues = (): string[] => {
    const values: string[] = [];
    if (accountingType.gross) values.push('G');
    if (accountingType.net) values.push('N');
    return values;
  };

  const getAccountingTypeForApi = (): string => {
    // If both Net and Gross are selected, return "A" (All)
    if (accountingType.net && accountingType.gross) {
      return 'A';
    }
    // If only Net is selected, return "N"
    if (accountingType.net) {
      return 'N';
    }
    // If only Gross is selected, return "G"
    if (accountingType.gross) {
      return 'G';
    }
    // Default to Net if none selected
    return 'N';
  };

  // Function to apply current filters to URL parameters
  const applyFiltersToUrl = () => {
    const newParams = new URLSearchParams();

    // Map PPO filter
    if (ppoValue) {
      newParams.append('ppo', ppoValue);
    } else {
      newParams.append('ppo', 'All');
    }

    // Map direction filter to table parameter
    newParams.append('table', ataFilter.toLowerCase());

    // Map year if available
    if (selectedYear) {
      newParams.append('year', selectedYear);
    } else {
      newParams.append('year', 'all');
    }

    // Add custom NO4-specific filters
    const mailCategories = getMailCategoryValues();
    const accountingTypes = getAccountingTypeValues();

    if (mailCategories.length > 0) {
      newParams.append('mailCategories', mailCategories.join(','));
    }

    if (accountingTypes.length > 0) {
      newParams.append('accountingTypes', accountingTypes.join(','));
    }

    setSearchParams(newParams);
  };



  const handleExport = (format: 'HTML' | 'PDF' | 'XLS') => {
    // Reset any previous errors
    resetReportError();

    // Validate required fields
    if (!selectedYear) {
      showSnackbar('Please select a Year');
      return;
    }

    // Validate required fields
    const mailCategories = getMailCategoryValues();
    const accountingTypes = getAccountingTypeValues();

    if (mailCategories.length === 0) {
      showSnackbar('Please select at least one Mail Category');
      return;
    }

    if (accountingTypes.length === 0) {
      showSnackbar('Please select at least one Accounting Type');
      return;
    }

    // Prepare API parameters
    const params = {
      mailCategories,
      accountingType: getAccountingTypeForApi(),
      formatType: format.toLowerCase() as 'html' | 'pdf' | 'xls',
      year: parseInt(selectedYear),
      direction: ataFilter === 'All' ? 'ALL' : ataFilter.toUpperCase(),
      ...(ppoValue && { designatedOperator: ppoValue }),
    };

    generateReport(params, {
      onSuccess: (response) => {
        if (format === 'HTML') {
          // For HTML, display the tables data received from the API
          console.log('HTML Report tables generated:', response.data);
          console.log('Current filter params used:', params);
          
          // Check for 204 No Content or empty data
          if (!response.data || (Array.isArray(response.data) && response.data.length === 0)) {
            // Set no data found state instead of clearing results
            setNoDataFound(true);
            setHtmlResults(null);
            // Don't apply filters to SectionBar when no data is found
            return;
          }

          if (response.data) {
            // Store the data in a variable to ensure type safety
            const newData: any[] = response.data;
            // Clear no data found state and set new data
            setNoDataFound(false);
            setHtmlResults(null);
            // Store the direction used for this report
            setResultsDirection(ataFilter);
            // Use setTimeout to ensure the state is cleared before setting new data
            setTimeout(() => {
              setHtmlResults(newData);
              console.log('Updated htmlResults state with new data:', newData);
            }, 0);
            
            // Apply current filters to URL parameters only when data is found
            applyFiltersToUrl();
          }
        } else {
          // For PDF/XLS, download is handled automatically in the hook
          console.log(
            `${format} Report downloaded successfully with filename: ${response.filename}`
          );
        }
      },
      onError: async (error: Error) => {
        console.error('Error generating report:', error);
        
        // Handle NOT_FOUND errors for PDF/XLS exports
        if (format !== 'HTML') {
          let errorMessage = 'An error occurred while generating the report';
          let errorStatus: string | undefined;
          
          // Check if it's an axios error
          if (axios.isAxiosError(error)) {
            // For blob responses, the error data might be a Blob containing JSON
            if (error.response?.data instanceof Blob) {
              try {
                const text = await error.response.data.text();
                const errorData = JSON.parse(text);
                errorMessage = errorData.message || errorMessage;
                errorStatus = errorData.status;
              } catch (parseError) {
                // If parsing fails, use default message
                console.error('Failed to parse error response:', parseError);
              }
            } else if (error.response?.data && typeof error.response.data === 'object') {
              // For non-blob responses, check the data directly
              const errorData = error.response.data as { message?: string; status?: string };
              errorMessage = errorData.message || errorMessage;
              errorStatus = errorData.status;
            }
            
            // Check status code as fallback
            if (!errorStatus && error.response?.status === 404) {
              errorStatus = 'NOT_FOUND';
            }
          }
          
          // Show snackbar with appropriate message
          if (errorStatus === 'NOT_FOUND' || errorMessage.includes('No NO4 reports available') || errorMessage.includes('No NO4')) {
            showSnackbar(
              errorMessage || 'No NO4 reports available for the selected criteria',
              'warning'
            );
          } else {
            showSnackbar(errorMessage, 'error');
          }
        } else {
          // For HTML format, show error snackbar
          showSnackbar(
            error instanceof Error ? error.message : 'Failed to generate report. Please try again.',
            'error'
          );
        }
      },
    });
  };

  // Memoize clearResults callback
  const clearResults = useCallback(() => {
    setHtmlResults(null);
    setNoDataFound(false);
    setResultsDirection('');
    // Clear URL parameters when results are cleared
    setSearchParams(new URLSearchParams());
  }, [setSearchParams]);

  // Use ref to track if we've already synced URL parameters
  const hasSyncedUrlParams = useRef(false);

  // Initial URL parameter sync when page loads
  useEffect(() => {
    // Only sync once and when yearOptions are available
    if (hasSyncedUrlParams.current || yearOptions.length === 0) {
      return;
    }

    const ppoParam = searchParams.get('ppo');
    const yearParam = searchParams.get('year');
    const tableParam = searchParams.get('table');
    const mailCategoriesParam = searchParams.get('mailCategories');
    const accountingTypesParam = searchParams.get('accountingTypes');

    // Sync PPO parameter
    if (ppoParam && ppoParam !== 'All') {
      setPpoValue(ppoParam);
    }

    // Sync direction filter from table parameter
    if (tableParam) {
      setAtaFilter(tableParam === 'inbound' ? 'Inbound' : tableParam === 'outbound' ? 'Outbound' : 'All');
    }

    // Sync year parameter
    if (yearParam && yearParam !== 'all') {
      setSelectedYear(yearParam);
    }

    // Sync mail categories (service types)
    if (mailCategoriesParam) {
      const categories = mailCategoriesParam.split(',');
      setServiceType({
        aircraftPriority: categories.includes('A'),
        salNonPriority: categories.includes('B'),
        surfaceNonPriority: categories.includes('C'),
        surfacePriority: categories.includes('D'),
      });
    }

    // Sync accounting types
    if (accountingTypesParam) {
      const types = accountingTypesParam.split(',');
      setAccountingType({
        gross: types.includes('G'),
        net: types.includes('N'),
      });
    }

    // Mark as synced
    hasSyncedUrlParams.current = true;
  }, [searchParams, yearOptions]);

  // Sync URL params with page state when SectionBar filters change (only when results exist)
  useEffect(() => {
    const ppoParam = searchParams.get('ppo');
    const yearParam = searchParams.get('year');
    const tableParam = searchParams.get('table');

    // Only sync if we have HTML results (filters are active)
    if (htmlResults) {
      if (ppoParam && ppoParam !== 'All' && ppoParam !== ppoValue) {
        setPpoValue(ppoParam);
      }

      if (yearParam && yearParam !== 'all' && yearParam !== selectedYear) {
        setSelectedYear(yearParam);
      }

      if (tableParam) {
        const newDirection = tableParam === 'inbound' ? 'Inbound' : tableParam === 'outbound' ? 'Outbound' : 'All';
        if (newDirection !== ataFilter) {
          setAtaFilter(newDirection);
        }
      }
    }
  }, [searchParams, htmlResults]);

  // Debug logging for htmlResults
  useEffect(() => {
    if (htmlResults) {
      console.log('htmlResults updated:', htmlResults);
    }
  }, [htmlResults]);

  // Helper functions to determine button text
  const getServiceTypeButtonText = () => {
    const allSelected =
      serviceType.aircraftPriority &&
      serviceType.salNonPriority &&
      serviceType.surfaceNonPriority &&
      serviceType.surfacePriority;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  const getAccountingTypeButtonText = () => {
    const allSelected = accountingType.gross && accountingType.net;
    return allSelected ? 'Deselect all' : 'Select all';
  };

  // Handler functions for SectionBar custom filter changes
  const handleSectionBarServiceTypesChange = useCallback((values: string[]) => {
    setServiceType({
      aircraftPriority: values.includes('A'),
      salNonPriority: values.includes('B'),
      surfaceNonPriority: values.includes('C'),
      surfacePriority: values.includes('D'),
    });
  }, []);

  const handleSectionBarAccountingTypesChange = useCallback((values: string[]) => {
    setAccountingType({
      gross: values.includes('G'),
      net: values.includes('N'),
    });
  }, []);

  const handleSectionBarYearChange = useCallback((year: string) => {
    setSelectedYear(year);
  }, []);

  // Handler function for SectionBar direction change
  const handleSectionBarDirectionChange = useCallback((direction: string) => {
    setAtaFilter(direction);
  }, []);

  // Handler function for SectionBar PPO change
  const handleSectionBarPpoChange = useCallback((ppo: string) => {
    setPpoValue(ppo);
  }, []);

  // Create custom filters object for SectionBar
  const customFiltersForSectionBar = useMemo(() => {
    // Only show SectionBar custom filters when we have actual results (not when no data found)
    if (!htmlResults || noDataFound) return undefined;

    return {
      serviceTypes: getMailCategoryValues(),
      accountingTypes: getAccountingTypeValues(),
      year: selectedYear,
      direction: ataFilter,
      ppo: ppoValue,
      ppoOptions: ppoOptions.map(option => ({
        id: option.id,
        name: option.label,
        disabled: false,
      })),
      isPpoLoading: isPpoLoading,
      yearOptions: yearOptions, // Pass cascaded year options
      onServiceTypesChange: handleSectionBarServiceTypesChange,
      onAccountingTypesChange: handleSectionBarAccountingTypesChange,
      onYearChange: handleSectionBarYearChange,
      onDirectionChange: handleSectionBarDirectionChange,
      onPpoChange: handleSectionBarPpoChange,
      onExport: handleExport,
      isExporting: isGeneratingReport,
    };
  }, [
    htmlResults,
    noDataFound,
    serviceType,
    accountingType,
    selectedYear,
    ataFilter,
    ppoValue,
    ppoOptions,
    isPpoLoading,
    getMailCategoryValues,
    getAccountingTypeValues,
    handleSectionBarServiceTypesChange,
    handleSectionBarAccountingTypesChange,
    handleSectionBarYearChange,
    handleSectionBarDirectionChange,
    handleSectionBarPpoChange,
    handleExport,
    isGeneratingReport,
  ]);

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column' }} data-testid='no4-report-container'>
      <Grid container>
        {/* First Row - SectionBar */}
        <Grid item xs={12}>
          <SectionBar
            title='NO4 Report'
            subtitle='IPC TD Accounting'
            onToggleSidebar={handleSidebarToggle}
            isSidebarOpen={sidebarOpen}
            showFilters={{
              ppo: !!htmlResults && !noDataFound,
              customNO4: !!htmlResults && !noDataFound,
            }}
            customFilters={customFiltersForSectionBar}
            data-testid='section-bar'
          />
        </Grid>

        {/* Second Row - Sidebar and MainContent */}
        <Grid item xs={12} sx={{ display: 'flex', position: 'relative' }}>
          {/* Sidebar */}
          {sidebarOpen && (
            <Box sx={{ position: 'relative' }}>
              <Sidebar isOpen={sidebarOpen} onToggle={handleSidebarToggle} navItems={navItems} data-testid="no4-report-sidebar" />
            </Box>
          )}

          {/* Main Content */}
          <MainContent sidebarOpen={sidebarOpen} data-testid='no4-report-main-content'>
            <ReportContainer data-testid="no4-report-content">
              {/* Header */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  mb: 3,
                }}
                data-testid="no4-report-header"
              >
                <Typography variant='h4' sx={{ fontWeight: 600, color: '#063142' }} data-testid="no4-report-title">
                  NO4 Report
                </Typography>
                <Box sx={{ display: 'flex', gap: 1 }} data-testid="no4-report-header-actions">
                  {(htmlResults || noDataFound) && (
                    <ClearResultsButton variant='outlined' onClick={clearResults} data-testid="no4-report-clear-results-btn">
                      Clear Results
                    </ClearResultsButton>
                  )}
                </Box>
              </Box>
              {/* Main Filter Section - Hide when HTML results exist (but show when no data found) */}
              {!htmlResults && (
                <>
                  {/* PPO Section */}
                  <FilterSection>
                    {/* PPO Dropdown and ATA Filter in one row */}
                    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: 1.5, mb: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          DO
                        </Typography>
                        <SelectContainer>
                          <FormControl size='small' fullWidth>
                            <Select
                              value={ppoValue}
                              onChange={(e) => handleDOChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 160 }}
                              disabled={isPpoLoading}
                            >
                              <MenuItem value='' disabled>
                                {isPpoLoading ? (
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <CircularProgress size={16} />
                                    <span>Loading...</span>
                                  </Box>
                                ) : (
                                  'Select DO'
                                )}
                              </MenuItem>
                              {!isPpoLoading &&
                                !ppoError &&
                                (selectedYear ? operatorsForYear : operators).map((targetDO) => (
                                  <MenuItem key={targetDO} value={targetDO}>
                                    <Box
                                      sx={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                        width: '100%',
                                      }}
                                    >
                                      <span>{targetDO}</span>
                                      
                                    </Box>
                                  </MenuItem>
                                ))}
                              {ppoError && <MenuItem disabled>Error loading PPOs</MenuItem>}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>

                      {/* ATA Filter using Segmented Control */}
                      <StyledSegmentedControl>
                        {directionOptions.map((option) => (
                          <StyledSegmentButton
                            key={option.id}
                            active={ataFilter === option.id}
                            onClick={() => handleDirectionChange(option.id)}
                            sx={{
                              color: ataFilter === option.id ? '#ffffff' : '#00B6BA',
                              minWidth: '70px',
                            }}
                          >
                            {option.label}
                          </StyledSegmentButton>
                        ))}
                      </StyledSegmentedControl>
                    </Box>

                    {/* Divider */}
                    <Box
                      sx={{
                        height: '1px',
                        backgroundColor: '#E1E8ED',
                        mb: 2,
                      }}
                    />

                    {/* Year Selector */}
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                        <Typography sx={{ fontSize: '14px', fontWeight: 700, color: '#063142' }}>
                          Year: *
                        </Typography>
                        <SelectContainer>
                          <FormControl size='small' fullWidth>
                            <Select
                              value={selectedYear}
                              onChange={(e) => handleYearChange(e.target.value)}
                              displayEmpty
                              sx={{ minWidth: 184 }}
                            >
                              <MenuItem value=''>Select</MenuItem>
                              {(ppoValue ? yearsForOperator.map(year => year.toString()) : yearOptions.map(opt => opt.value)).map((year) => (
                                <MenuItem key={year} value={year}>
                                  {year}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </SelectContainer>
                      </Box>
                    </Box>
                  </FilterSection>

                  <Box
                    sx={{
                      height: '1px',
                      backgroundColor: '#E1E8ED',
                      mb: 2,
                    }}
                  />

                  {/* Main Content Grid - without borders, with dividers */}
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: { xs: 'column', md: 'row' },
                      width: '100%',
                    }}
                  >
                    {/* Mail Category */}
                    <Box
                      sx={{
                        flex: { xs: 'none', md: '1 1 0' },
                        padding: 2,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1.5,
                        }}
                      >
                        <SectionTitle>Mail Category</SectionTitle>
                        <Link
                          component='button'
                          onClick={handleSelectAllServiceType}
                          sx={{
                            color: '#00ACE7',
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#E3F7FE',
                              color: '#0095C7',
                              textDecoration: 'none',
                            },
                            '&:active': {
                              backgroundColor: '#B3E5FC',
                            },
                          }}
                        >
                          {getServiceTypeButtonText()}
                        </Link>
                      </Box>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={serviceType.aircraftPriority}
                          onChange={handleServiceTypeChange('aircraftPriority')}
                          label='A - Airmail Priority'
                        />
                        <CustomCheckbox
                          checked={serviceType.salNonPriority}
                          onChange={handleServiceTypeChange('salNonPriority')}
                          label='B - SAL Non-Priority'
                        />
                        <CustomCheckbox
                          checked={serviceType.surfaceNonPriority}
                          onChange={handleServiceTypeChange('surfaceNonPriority')}
                          label='C - Surface Non-Priority'
                        />
                        <CustomCheckbox
                          checked={serviceType.surfacePriority}
                          onChange={handleServiceTypeChange('surfacePriority')}
                          label='D - Surface Priority'
                        />
                      </CheckboxGroup>
                    </Box>

                    {/* Vertical Divider */}
                    <Box
                      sx={{
                        width: '1px',
                        backgroundColor: '#E1E8ED',
                        display: { xs: 'none', md: 'block' },
                      }}
                    />

                    {/* Horizontal Divider for mobile */}
                    <Box
                      sx={{
                        height: '1px',
                        backgroundColor: '#E1E8ED',
                        my: 2,
                        display: { xs: 'block', md: 'none' },
                      }}
                    />

                    {/* Accounting Type */}
                    <Box
                      sx={{
                        flex: { xs: 'none', md: '1 1 0' },
                        padding: 2,
                        minWidth: 0,
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          mb: 1.5,
                        }}
                      >
                        <SectionTitle>Accounting Type</SectionTitle>
                        <Link
                          component='button'
                          onClick={handleSelectAllAccountingType}
                          sx={{
                            color: '#00ACE7',
                            textDecoration: 'none',
                            fontSize: '12px',
                            fontWeight: 500,
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#E3F7FE',
                              color: '#0095C7',
                              textDecoration: 'none',
                            },
                            '&:active': {
                              backgroundColor: '#B3E5FC',
                            },
                          }}
                        >
                          {getAccountingTypeButtonText()}
                        </Link>
                      </Box>
                      <CheckboxGroup>
                        <CustomCheckbox
                          checked={accountingType.net}
                          onChange={handleAccountingTypeChange('net')}
                          label='Net'
                        />
                        <CustomCheckbox
                          checked={accountingType.gross}
                          onChange={handleAccountingTypeChange('gross')}
                          label='Gross'
                        />
                      </CheckboxGroup>
                    </Box>
                  </Box>

                  {/* Action Buttons */}
                  <ButtonContainer sx={{ justifyContent: 'flex-end' }}>
                    <ResetButton variant='contained' onClick={handleReset}>
                      RESET
                    </ResetButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('HTML')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'HTML'}
                    </ExportButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('PDF')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'PDF'}
                    </ExportButton>
                    <ExportButton
                      variant='contained'
                      onClick={() => handleExport('XLS')}
                      disabled={isGeneratingReport}
                    >
                      {isGeneratingReport ? <CircularProgress size={16} /> : 'XLS'}
                    </ExportButton>
                  </ButtonContainer>
                </>
              )}

              {/* No Data Found Section */}
              {noDataFound && (
                <NoDataFound
                  title="No Data Available"
                  message="No NO4 report data was found for the selected criteria. Please try adjusting your filters or selecting a different year."
                  onClearResults={clearResults}
                  onResetFilters={handleReset}
                />
              )}

              {/* Results Section - Show tables directly under filters */}
              {htmlResults && !noDataFound && (
                <Box sx={{ mt: 4 }}>
                  {htmlResults.map((reportData, index) => {
                    // Determine direction for this specific table based on its receiving PPO
                    const tableDirection = determineDirection(
                      ataFilter,
                      currentUserDesignatedOperator,
                      reportData?.no3Cn56Summary?.receivingPpo
                    );
                    
                    return (
                      <NO4ReportTable 
                        key={index} 
                        data={reportData} 
                        direction={tableDirection} 
                        tableIndex={index} 
                      />
                    );
                  })}
                </Box>
              )}
            </ReportContainer>
          </MainContent>
        </Grid>
      </Grid>

      {/* Notification Snackbar */}
      <NotificationSnackbar
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={handleSnackbarClose}
        data-testid='no4-report-snackbar'
      />
    </Box>
  );
}

export default NO4Report;